import useSWR from "swr";
import { z } from "zod";
import { Network } from "@/utils/network";
import { zodUnsafeValidate } from "@/utils/zod";
import { InstagramSearch } from "../types";
import { useInstagramSearch } from "./useInstagramSearch";
import { useInstagramSearchForm } from "./useInstagramSearchForm";

const InstagramSearchRegionsResponseSchema = z.object({
  regions: InstagramSearch.OptionSchema.array(),
});

type InstagramSearchRegionsResponse = z.infer<
  typeof InstagramSearchRegionsResponseSchema
>;

export const useInstagramSearchRegions = (query: string) => {
  const config = useInstagramSearch((state) => state.config);
  const country = useInstagramSearchForm((state) => state.country);
  const setRegions = useInstagramSearchForm((state) => state.setRegions);

  return useSWR(
    () => {
      if (country === undefined) {
        return null;
      }

      return [config.api.getRegions, country, query];
    },
    async ([api, country]) => {
      const { data } = await Network.get<InstagramSearchRegionsResponse>(api, {
        country: country,
        query,
      });

      return zodUnsafeValidate(InstagramSearchRegionsResponseSchema, data);
    },
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnMount: true,
      revalidateOnReconnect: true,
      shouldRetryOnError: false,
      keepPreviousData: true,

      onSuccess: (data) => {
        const selection = useInstagramSearchForm.getState().regions;
        const selectedRegions = data.regions
          .filter((region) => selection.has(region.id))
          .map((region) => region.id);

        setRegions(selectedRegions);
      },
    },
  );
};
